import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

// Ready case ids the user has already been made aware of (baseline + notified).
const NOTIFIED_KEY = "pd_care_notified";
// Full notifications feed for the header bell: [{caseId, title, at, read}].
const FEED_KEY = "pd_care_feed";
const EVENT = "care-notify-change";

const readSet = (k) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(k) || "[]"));
  } catch {
    return new Set();
  }
};
const writeSet = (k, set) => localStorage.setItem(k, JSON.stringify([...set]));

const readFeed = () => {
  try {
    return JSON.parse(localStorage.getItem(FEED_KEY) || "[]");
  } catch {
    return [];
  }
};
const writeFeed = (feed) => localStorage.setItem(FEED_KEY, JSON.stringify(feed.slice(0, 30)));
const emit = () => window.dispatchEvent(new Event(EVENT));

export function getNotifications() {
  return readFeed();
}

export function getUnreadCount() {
  return readFeed().filter((n) => !n.read).length;
}

function pushNotification(caseId, title) {
  const feed = readFeed().filter((n) => n.caseId !== caseId);
  feed.unshift({ caseId, title, at: new Date().toISOString(), read: false });
  writeFeed(feed);
}

// Called when a user opens a ready case — marks its feed item read + suppresses future toasts.
export function markCaseSeen(id) {
  const notified = readSet(NOTIFIED_KEY);
  if (!notified.has(id)) {
    notified.add(id);
    writeSet(NOTIFIED_KEY, notified);
  }
  const feed = readFeed();
  let changed = false;
  feed.forEach((n) => {
    if (n.caseId === id && !n.read) {
      n.read = true;
      changed = true;
    }
  });
  if (changed) {
    writeFeed(feed);
    emit();
  }
}

export function markAllNotificationsRead() {
  const feed = readFeed();
  let changed = false;
  feed.forEach((n) => {
    if (!n.read) {
      n.read = true;
      changed = true;
    }
  });
  if (changed) {
    writeFeed(feed);
    emit();
  }
}

// Live unread-count for the bell/badge; updates on notify + cross-tab storage events.
export function useUnreadCount() {
  const [n, setN] = useState(getUnreadCount());
  useEffect(() => {
    const h = () => setN(getUnreadCount());
    window.addEventListener(EVENT, h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener(EVENT, h);
      window.removeEventListener("storage", h);
    };
  }, []);
  return n;
}

export function useNotifications() {
  const [feed, setFeed] = useState(getNotifications());
  useEffect(() => {
    const h = () => setFeed(getNotifications());
    window.addEventListener(EVENT, h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener(EVENT, h);
      window.removeEventListener("storage", h);
    };
  }, []);
  return feed;
}

// Polls cases while mounted; nudges + records a feed entry when an opinion becomes ready.
export function useCareNotificationPoller(navigate) {
  const poll = useCallback(async () => {
    let cases;
    try {
      const { data } = await api.get("/care/cases");
      cases = data || [];
    } catch {
      return;
    }
    const ready = cases.filter((c) => c.status === "opinion_ready");

    // First-ever run: baseline all currently-ready cases silently (no toast flood, no feed spam).
    if (localStorage.getItem(NOTIFIED_KEY) === null) {
      writeSet(NOTIFIED_KEY, new Set(ready.map((c) => c.id)));
      return;
    }

    const notified = readSet(NOTIFIED_KEY);
    const path = window.location.pathname;
    let newlyReady = false;

    ready.forEach((c) => {
      if (notified.has(c.id)) return;
      notified.add(c.id);
      const viewing = path.includes(`/care/cases/${c.id}`);
      if (!viewing) {
        pushNotification(c.id, c.title);
        newlyReady = true;
        toast.success(`Second opinion ready — ${c.title}`, {
          description: "Your specialist's review is now available.",
          action: {
            label: "View",
            onClick: () => (navigate ? navigate(`/care/cases/${c.id}`) : (window.location.href = `/care/cases/${c.id}`)),
          },
        });
      }
    });

    writeSet(NOTIFIED_KEY, notified);
    if (newlyReady) emit();
  }, [navigate]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 8000);
    return () => clearInterval(id);
  }, [poll]);
}
